/** Meta (Facebook) Pixel ID for this landing page. */
export const PIXEL_ID = '950234311449823'

type FbqArgs = unknown[]

interface FbqFunction {
  (...args: FbqArgs): void
  callMethod?: (...args: FbqArgs) => void
  queue?: FbqArgs[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: FbqFunction
    _fbq?: FbqFunction
  }
}

let initialised = false

/**
 * Meta Pixel base code, ported from the official snippet.
 *
 * Runs at module load time (see index.tsx) so fbevents.js is requested and
 * `init` + `PageView` are queued before React mounts — that is what the Meta
 * Pixel Helper and Events Manager look for.
 */
export function loadPixel(): void {
  if (typeof window === 'undefined' || initialised) return
  initialised = true

  if (!window.fbq) {
    const fbq: FbqFunction = function (...args: FbqArgs) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args)
      } else {
        fbq.queue?.push(args)
      }
    }

    fbq.queue = []
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'

    window.fbq = fbq
    if (!window._fbq) window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  window.fbq?.('init', PIXEL_ID)
  window.fbq?.('track', 'PageView')
}

/**
 * Loads the pixel once the browser is idle (or on the first interaction,
 * whichever comes first). This keeps fbevents.js off the critical path — it
 * would otherwise compete with the hero image and delay LCP — while still
 * registering the pixel and its PageView on every visit.
 */
export function loadPixelWhenIdle(): void {
  if (typeof window === 'undefined' || initialised) return

  let done = false
  const run = () => {
    if (done) return
    done = true
    window.removeEventListener('pointerdown', run)
    window.removeEventListener('keydown', run)
    window.removeEventListener('scroll', run)
    loadPixel()
  }

  // First meaningful interaction should never be missed.
  window.addEventListener('pointerdown', run, { once: true, passive: true })
  window.addEventListener('keydown', run, { once: true })
  window.addEventListener('scroll', run, { once: true, passive: true })

  const schedule = () => {
    const idle = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void
      }
    ).requestIdleCallback
    if (idle) {
      idle(run, { timeout: 3000 })
    } else {
      window.setTimeout(run, 1500)
    }
  }

  if (document.readyState === 'complete') {
    schedule()
  } else {
    window.addEventListener('load', schedule, { once: true })
  }
}

/** Fires a standard or custom pixel event. Safe to call before fbevents loads. */
export function trackPixelEvent(
  event: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return
  loadPixel()
  window.fbq?.('track', event, params)
}
